<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  getCarrierCompanyName,
  getCarrierCompanyScac
} from '@satellite/composables/useCarrierDisplay';

import { useDialogTrigger } from '@/composables';
import { useCompanySelectSearch } from '@/composables/useCompanySelectSearch';
import { useEntitySelectModel } from '@/composables/useEntitySelectModel';

import type {
  CarrierCompanyDetails,
  CarrierUserDetails
} from '@satellite/composables/carrierPresenterShared';
import type { EntitySelectModelValue } from '@/composables/useEntitySelectModel';

/** A company typed in rather than picked (`allowCustomCompany`): it has no record, so no `id`. */
type CustomCompany = { id?: string | null; name?: string };

defineOptions({ inheritAttrs: false });

const {
  modelValue,
  clearable = false,
  multiple = false,
  allowCreate = false,
  allowCustomCompany = false,
  createEntryPoint = 'Carrier Company Select List'
} = defineProps<{
  /** One company, or many when `multiple`. */
  modelValue: EntitySelectModelValue<CarrierCompanyDetails> | CustomCompany | undefined;
  /** Allow user to clear the selection. */
  clearable?: boolean;
  /** Selects many companies, with select-all and a counted selection summary. */
  multiple?: boolean;
  /** Offers a "create new company" row at the end of the list. */
  allowCreate?: boolean;
  /**
   * Offers the typed search as a company of its own, emitted as `{ id: null, name }`. Only for
   * consumers that store a free-text company (e.g. the yard's `companyHint`); single-select only.
   */
  allowCustomCompany?: boolean;
  /** Mixpanel context forwarded to the create-company dialog. */
  createEntryPoint?: string;
}>();

const emit = defineEmits<{
  'update:model-value': [company: EntitySelectModelValue<CarrierCompanyDetails> | CustomCompany];
}>();

const attrs = useAttrs();
const { t } = useI18n();

/**
 * Selection and search only read `id` and `name`, which a custom company has too (its `id` is
 * just empty), so it goes through them as a regular company.
 */
const companyModel = computed(
  () => (modelValue ?? null) as EntitySelectModelValue<CarrierCompanyDetails>
);

const selectedCompany = computed(() =>
  Array.isArray(companyModel.value) ? (companyModel.value[0] ?? null) : companyModel.value
);
const {
  data: companies,
  error,
  isLoading,
  isSearchBelowMinimum,
  minimumSearchLength,
  onIntersect,
  search,
  updateSearch
} = useCompanySelectSearch({ selectedCompany });

const placeholder = computed(() =>
  t('forms.elements.selects.companySelect.placeholder', {
    n: minimumSearchLength
  })
);

const hint = computed(() =>
  isSearchBelowMinimum.value
    ? t('forms.elements.selects.companySelect.messages.searchHintBelowMinimum', {
        n: minimumSearchLength
      })
    : ''
);

const noDataMessage = computed(() => {
  if (isSearchBelowMinimum.value) {
    return t('forms.elements.selects.companySelect.messages.typeAtLeastNCharactersToSearch', {
      n: minimumSearchLength
    });
  }

  if (error.value) {
    return t('forms.elements.selects.companySelect.messages.searchFailed');
  }

  if (search.value) {
    return t('forms.elements.selects.companySelect.messages.noMatchingCompaniesFound');
  }

  return placeholder.value;
});

/**
 * The display getters expect a carrier contact whose `company` holds these
 * fields, so each list row is wrapped rather than re-implementing name/SCAC.
 */
const asCarrierUser = (company: CarrierCompanyDetails): CarrierUserDetails => ({
  id: company.id,
  company
});

const getCompanyName = (company: CarrierCompanyDetails) =>
  getCarrierCompanyName(asCarrierUser(company)) ?? '';

const getCompanyScac = (company: CarrierCompanyDetails) =>
  getCarrierCompanyScac(asCarrierUser(company));

const {
  autocompleteModelValue,
  getItemIcon,
  isMultiple,
  onSelectionUpdate,
  selectAllIcon,
  selectedItems,
  selectionSummary,
  selectOnly,
  toggleSelectAll
} = useEntitySelectModel<CarrierCompanyDetails>({
  modelValue: companyModel,
  items: companies,
  multiple: () => multiple,
  getItemTitle: getCompanyName,
  getCountLabel: count => t('global.specialtyTerms.companies', count),
  onUpdate: value => emit('update:model-value', value)
});

const showSelectAllRow = computed(() => multiple && companies.value.length > 0);

/**
 * A company created from the list is selected right away, so the menu has to
 * close with it — otherwise the user lands back on a stale result list.
 */
const isMenuOpen = ref(false);

/** The dialog event types its payload as `unknown`, hence the narrowing. */
const handleCompanyCreated = (company: unknown) => {
  const createdCompany = company as CarrierCompanyDetails | null;

  if (!createdCompany?.id) {
    return;
  }

  isMenuOpen.value = false;
  onSelectionUpdate(isMultiple.value ? [...selectedItems.value, createdCompany] : createdCompany);
};

/**
 * Hidden while a search is in flight so the row cannot be picked for a term the results are
 * about to answer, and when the term is just the selected company's name echoed back into the
 * input by Vuetify.
 */
const showCustomCompanyRow = computed(() => {
  if (!allowCustomCompany || multiple || isLoading.value || !search.value) {
    return false;
  }

  const selectedName = selectedCompany.value?.name ?? '';

  return search.value.toLowerCase() !== selectedName.toLowerCase();
});

/** A free-text company has no record yet, so consumers tell it apart by its missing `id`. */
const selectCustomCompany = () => {
  const customCompany: CustomCompany = { id: null, name: search.value };

  isMenuOpen.value = false;
  onSelectionUpdate(customCompany);
};

const openCreateCompanyDialog = () => {
  const { triggerDialog } = useDialogTrigger('open-create-carrier-company-dialog');

  triggerDialog({
    mixpanelEntryPoint: createEntryPoint,
    onCompanyCreated: handleCompanyCreated
  });
};
</script>

<template>
  <v-autocomplete
    no-filter
    return-object
    color="primary"
    data-testid="select-company-search-field"
    density="compact"
    hide-details="auto"
    item-value="id"
    v-bind="attrs"
    v-intersect="{ handler: onIntersect }"
    v-model:menu="isMenuOpen"
    variant="outlined"
    :clearable="clearable"
    :hint="hint"
    :item-title="getCompanyName"
    :items="companies"
    :loading="isLoading"
    :model-value="autocompleteModelValue"
    :multiple="multiple"
    :persistent-hint="isSearchBelowMinimum"
    :placeholder="placeholder"
    @keydown.enter.prevent="() => {}"
    @update:model-value="onSelectionUpdate"
    @update:search="updateSearch">
    <template
      v-if="showCustomCompanyRow || showSelectAllRow || $slots['prepend-item']"
      #prepend-item>
      <template v-if="showCustomCompanyRow">
        <v-list-item
          role="option"
          data-testid="company-select-use-custom-company"
          @click="selectCustomCompany">
          <v-list-item-title class="typography-body-md">
            {{
              t('forms.elements.selects.companySelect.messages.selectAsCarrier', { name: search })
            }}
          </v-list-item-title>
        </v-list-item>

        <v-divider />
      </template>

      <v-list-item
        v-if="showSelectAllRow"
        ripple
        class="typography-body-md"
        data-testid="company-select-select-all"
        :title="t('forms.common.actions.selectAll')"
        @click="toggleSelectAll">
        <template #prepend>
          <v-icon :icon="selectAllIcon" />
        </template>
      </v-list-item>

      <slot name="prepend-item" />

      <v-divider v-if="showSelectAllRow" class="mt-2" />
    </template>

    <template #selection="{ item, index }">
      <span v-if="!multiple" class="typography-body-md" data-testid="company-select-selection">
        {{ getCompanyName(item.raw) }}
      </span>
      <span
        v-else-if="index === 0"
        class="typography-body-md d-inline-flex align-center ga-1"
        data-testid="company-select-selection">
        {{ selectionSummary.text }}
        <v-badge
          v-if="selectionSummary.count !== null"
          inline
          color="neutral-80"
          :content="selectionSummary.count" />
      </span>
    </template>

    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" ripple role="option" title="">
        <template v-if="multiple" #prepend>
          <v-icon :icon="getItemIcon(item.raw)" />
        </template>

        <template #title>
          <span class="typography-body-md">
            {{ getCompanyName(item.raw) }}
          </span>
        </template>
        <template v-if="getCompanyScac(item.raw)" #subtitle>
          <span
            data-testid="company-select-scac"
            class="typography-body-sm text--color-text-secondary">
            {{ getCompanyScac(item.raw) }}
          </span>
        </template>

        <template v-if="multiple" #append>
          <v-btn
            class="text-none typography-button-sm ml-auto"
            size="small"
            variant="text"
            data-testid="company-select-only"
            @click="selectOnly($event, item.raw)">
            {{ t('global.basicTerms.only') }}
          </v-btn>
        </template>
      </v-list-item>
    </template>

    <template #no-data>
      <v-list-item>
        <v-list-item-title>
          {{ noDataMessage }}
        </v-list-item-title>
      </v-list-item>
    </template>

    <template v-if="allowCreate || $slots['append-item']" #append-item>
      <v-divider class="mt-2" />

      <slot name="append-item" />

      <v-list-item
        v-if="allowCreate"
        data-testid="company-select-create-new-item"
        :disabled="isLoading"
        @click="openCreateCompanyDialog">
        <v-list-item-title>
          <span class="d-flex flex-row gap-1 align-center typography-body-md">
            <span>
              {{ t('forms.elements.selects.companySelect.messages.didntFindCarrierCompany') }}
            </span>
            <span class="typography-body-md-bold">
              {{ t('forms.elements.selects.companySelect.buttons.createNew') }}
            </span>
          </span>
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>
